/**
 * Feedback Widget Hook
 * Controls feedback widget state and submission logic
 */

import { useState, useCallback } from 'react';
import { getSupabaseClient } from '@/lib/supabase/loader';
import { submitFeedback } from '@/lib/feedback';
import { captureException } from '@/lib/sentry';

export interface FeedbackSubmission {
  title: string;
  description: string;
  feedback_type: string;
  category: string;
  sentiment: 'positive' | 'neutral' | 'negative';
  context: Record<string, unknown>;
}

export function useFeedbackWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async (data: FeedbackSubmission) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const supabase = await getSupabaseClient();

      // Get current user and org
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user's organization
      const { data: orgMember } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id)
        .limit(1)
        .single();

      const organizationId = orgMember?.organization_id;
      if (!organizationId) {
        throw new Error('No organization found');
      }

      // Submit feedback
      await submitFeedback({
        title: data.title,
        description: data.description,
        feedback_type: data.feedback_type as 'usability' | 'compliance' | 'integration' | 'feature_request' | 'bug_report' | 'performance',
        category: data.category as 'feature' | 'ui_ux' | 'performance' | 'security' | 'regulatory' | 'integration' | 'documentation',
        organization_id: organizationId,
        source: 'in_app',
        severity:
          data.sentiment === 'negative'
            ? 'high'
            : data.sentiment === 'positive'
              ? 'low'
              : 'medium',
        priority: 'backlog',
        context: {
          ...data.context,
          sentiment: data.sentiment,
          timestamp: new Date().toISOString(),
        },
      });

      setSubmitted(true);

      // Reset after 3 seconds
      setTimeout(() => {
        setSubmitted(false);
        setIsOpen(false);
      }, 3000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit feedback';
      setError(message);
      captureException(err, {
        tags: { area: 'feedback.widget' },
        extra: { data },
      });
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return {
    isOpen,
    setIsOpen,
    isSubmitting,
    submitted,
    error,
    handleSubmit,
  };
}
